import React from 'react';
import { useTranslation } from 'react-i18next';
import { BasicTooltip } from '@nivo/tooltip';
import NivoBarChart from '../charts/NivoBarChart';
import { ComparisonDetailMembersDataType } from '../../types';
import ContentSection from '../common/ContentSection/ContentSection';
import useDimensions from '../../hooks/useDimensions';
import useElementWidth from '../../hooks/useElementWidth';
import { useFormatters } from '../../utils/texts';
import { buildWrappedLegends, legendMarginBottom, AXIS_TITLE_HEIGHT } from '../../utils/nivoLegend';
import { sparseTickValues } from '../../utils/nivoTicks';

type Props = {
    comparison: ComparisonDetailMembersDataType['comparison'];
};

const MatchChartSection = ({ comparison }: Props) => {
    const [chartRef, containerWidth] = useElementWidth<HTMLDivElement>();
    const { width } = useDimensions();
    const { t, i18n } = useTranslation(['analyses', 'meetings']);
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);

    if (!comparison) {
        return null;
    }

    const ratios = Object.entries(comparison.data).map(([key, value], i) => {
        const neshodaLength = value.neshoda?.length || 0;
        const shodaLength = value.shoda?.length || 0;
        const totalLength = neshodaLength + shodaLength;
        const ratio = totalLength > 0 ? (shodaLength / totalLength) * 100 : 0;
        return { category: comparison.zasedani[i]?.name ?? key, ratio };
    });

    const style = getComputedStyle(document.body);
    const colors = [style.getPropertyValue('--clr-1'), style.getPropertyValue('--clr-7')];

    const barKey = 'meetingsAgreement';
    const barData = ratios.map((ratio) => ({ category: ratio.category, [barKey]: Number(ratio.ratio.toFixed(1)) }));

    const layout: 'horizontal' | 'vertical' = ratios.length > 30 && width <= 576 ? 'horizontal' : 'vertical';
    const height = layout === 'horizontal' ? `${Math.min(1000, Math.max(300, ratios.length * 22))}px` : '400px';

    const percentFormat = (value: number) => formatter.percentFrom100(value);

    // As with the meetings axis on UnityLineChart: Nivo renders one tick per category
    // unconditionally, so many meetings need thinning (+rotation in vertical mode,
    // where category labels run along the bottom) to stay legible.
    const categories = ratios.map((r) => r.category);
    const manyCategories = categories.length > 15;
    const categoryAxis = manyCategories
        ? { tickValues: sparseTickValues(categories, 15), tickRotation: layout === 'vertical' ? -40 : 0 }
        : {};
    const valueAxis = { format: percentFormat };

    const leftMargin = layout === 'horizontal' ? 140 : 60;

    const categoryAxisTitle = t('meetings', { ns: 'meetings' });
    const valueAxisTitle = t('comparison.MatchChartSection.meetingsAgreement');

    // Reserve the same tick-label zone as before, then extra room for the axis title
    // and for a legend that can now wrap onto more than one row on narrow screens.
    const tickZoneStartY = (layout === 'vertical' ? (manyCategories ? 130 : 90) : 70) - 25;
    const legendStartY = tickZoneStartY + AXIS_TITLE_HEIGHT;

    // Rotated (-40deg) tick labels in vertical+manyCategories mode can extend well past
    // the default axis-title offset, so give the title extra clearance in that case.
    const rotatedTicks = layout === 'vertical' && manyCategories;
    const resolvedAxisBottom = (() => {
        const base = layout === 'vertical' ? categoryAxis : valueAxis;
        return rotatedTicks ? { ...base, legendOffset: 55 } : base;
    })();
    const resolvedAxisLeft = layout === 'vertical' ? valueAxis : categoryAxis;

    const { legends, rowCount: legendRowCount } = buildWrappedLegends(
        [
            { id: 'meetingsAgreement', label: t('comparison.MatchChartSection.meetingsAgreement'), color: colors[0] },
            { id: 'avgAgreement', label: t('comparison.MatchChartSection.avgAgreement'), color: colors[1] }
        ],
        { containerWidth: containerWidth || undefined, itemWidth: 220, maxLabelLength: 30, startY: legendStartY }
    );
    const bottomMargin = legendMarginBottom(legendRowCount, { startY: legendStartY });

    return (
        <ContentSection
            level={3}
            title={t('comparison.MatchChartSection.agreementOnMeetings')}
            subtitle={t('comparison.MatchChartSection.agreementOnMeetingsTooltip')}
            className='dashboard-card'
        >
            <div ref={chartRef}>
                <NivoBarChart
                    height={height}
                    data={barData}
                    keys={[barKey]}
                    indexBy='category'
                    layout={layout}
                    colors={colors[0]}
                    valueScale={{ type: 'linear', min: 0, max: 100 }}
                    margin={{ top: 20, right: 20, bottom: bottomMargin, left: leftMargin }}
                    axisBottom={resolvedAxisBottom}
                    axisLeft={resolvedAxisLeft}
                    xTitle={layout === 'vertical' ? categoryAxisTitle : valueAxisTitle}
                    yTitle={layout === 'vertical' ? valueAxisTitle : categoryAxisTitle}
                    tooltip={({ indexValue, value, color }) => (
                        <BasicTooltip id={`${indexValue}: ${percentFormat(Number(value))}`} color={color} enableChip />
                    )}
                    markers={[
                        {
                            axis: layout === 'vertical' ? 'y' : 'x',
                            value: comparison.match_stat,
                            lineStyle: { stroke: colors[1], strokeWidth: 2, strokeDasharray: '10, 5' },
                            legend: t('comparison.MatchChartSection.avgAgreement')
                        }
                    ]}
                    legends={legends}
                />
            </div>
        </ContentSection>
    );
};

export default MatchChartSection;
